import { useEffect, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { setLenis } from "@/lib/smooth-scroll";
import { CustomCursor } from "@/components/site/CustomCursor";
import { ScrollProgress } from "@/components/site/ScrollProgress";
import { SectionCounter } from "@/components/site/SectionCounter";
import { Navbar } from "@/components/site/Navbar";
import { Hero } from "@/components/site/Hero";
import { Capabilities } from "@/components/site/Capabilities";
import { Approach } from "@/components/site/Approach";
import { Industries } from "@/components/site/Industries";
import { References } from "@/components/site/References";
import { CTASection } from "@/components/site/CTASection";
import { Footer } from "@/components/site/Footer";

gsap.registerPlugin(ScrollTrigger);

export default function Landing() {
  const [activeSection, setActiveSection] = useState(0);

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const isTouch = window.matchMedia("(hover: none) and (pointer: coarse)").matches;
    // Disable Lenis on touch devices — it interferes with native taps and form input
    if (reduced || isTouch) return undefined;

    const lenis = new Lenis({ autoRaf: false, lerp: 0.1 });
    setLenis(lenis);
    lenis.on("scroll", ScrollTrigger.update);
    const raf = (time) => lenis.raf(time * 1000);
    gsap.ticker.add(raf);
    gsap.ticker.lagSmoothing(0);

    return () => {
      gsap.ticker.remove(raf);
      lenis.destroy();
      setLenis(null);
    };
  }, []);

  useEffect(() => {
    const els = document.querySelectorAll("[data-section-idx]");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(Number(entry.target.dataset.sectionIdx));
          }
        });
      },
      { rootMargin: "-40% 0px -40% 0px" }
    );
    els.forEach((el) => observer.observe(el));

    const onLoad = () => ScrollTrigger.refresh();
    window.addEventListener("load", onLoad);
    return () => {
      observer.disconnect();
      window.removeEventListener("load", onLoad);
    };
  }, []);

  return (
    <div className="dl-page" data-testid="landing-page">
      <CustomCursor />
      <ScrollProgress />
      <SectionCounter active={activeSection} />
      <Navbar active={activeSection} />
      <main>
        <Hero />
        <Capabilities />
        <Approach />
        <Industries />
        <References />
        <CTASection />
      </main>
      <Footer />
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#2B2825",
            color: "#F5F1E8",
            border: "1px solid rgba(212,169,92,0.35)",
            fontFamily: "'Figtree', sans-serif",
          },
        }}
      />
    </div>
  );
}
